import { useCallback, useEffect, useMemo, useState } from 'react';

import { equals } from 'ramda';

import { confirmAlert } from 'utils/alert';

import { IBaseSearchFilterFormHookProps, IFilter } from '../types';

export const useBaseSearchFilterForm = ({
  filter,
  defaultFilter,
  onSave,
  onClose,
  type,
}: IBaseSearchFilterFormHookProps) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initFilterData = useMemo(() => filter.data, []);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [currentFilter, setCurrentFilter] = useState<IFilter>(filter);
  const isDefaultFilter = useMemo(() => {
    return equals(defaultFilter.data, currentFilter.data);
  }, [currentFilter.data, defaultFilter.data]);

  const handleChange = useCallback(
    /*@ts-ignore*/
    (newFilterData) => {
      const newCurrentFilterData = { ...currentFilter.data, ...newFilterData };
      setCurrentFilter({ ...currentFilter, data: newCurrentFilterData });
    },
    [currentFilter, initFilterData],
  );

  const handleClose = useCallback(() => {
    if (hasChanges) {
      confirmAlert({
        title: (
          <>
            You’ve made some new changes without saving them.
            <br />
            Are you sure you don’t want to save these new changes?
          </>
        ),
        onConfirm: onClose,
        confirmButtonText: 'Yes, I’m sure',
        cancelButtonText: 'No, keep them!',
      });
    } else {
      onClose();
    }
  }, [hasChanges, onClose]);

  const handleReset = useCallback(() => {
    setCurrentFilter({ ...currentFilter, data: defaultFilter.data });
  }, [currentFilter, defaultFilter.data]);

  useEffect(() => {
    setHasChanges(!equals(currentFilter.data, initFilterData));
  }, [currentFilter.data, initFilterData]);

  const handleSave = useCallback(async () => {
    await onSave(currentFilter);
    onClose();
  }, [currentFilter, onSave, onClose]);

  const layout = getSpecificPageStyles(type);

  return {
    layout,
    hasChanges,
    isDefaultFilter,
    currentFilter,
    handleChange,
    handleSave,
    handleReset,
    handleClose,
  };
};

const getSpecificPageStyles = (pageType: string) => {
  switch (pageType) {
    case 'media':
      return { text: 'Media', button: 'danger', radio: 'radio-media' };
    default:
      return { text: 'Content', button: 'secondary2', radio: 'radio-content' };
  }
};
